import React, { useContext, useEffect, useState } from 'react'
import { Layout } from './Layout'
import { Overview } from '../components/common/overview/Overview'
import { Button, makeStyles, Tab } from '@fluentui/react-components'
import { AddCircle24Regular } from '@fluentui/react-icons'
import { deleteFinancialYear, getRiskEconomyColumns, RiskEconomyTabs } from '../helpers/riskEconomyHelper'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from '../i18n/i18n'
import { MoreOptionsMenuButton } from '../components/common/other/MoreOptionsMenuButton'
import { IListState } from '../interfaces/IListState'
import { FinancialYearStatus, FinancialYear, Workplace } from '../api/schemas/schema'
import { DataContext } from '../context/DataContext'
import { api } from '../helpers/apiHelper'
import { ModalAddFinancialYear } from '../components/riskEconomy/ModalAddFinancialYear'
import { NavigationLink } from '../interfaces/INavigationLink'
import { isFinancialYearOwnerOrCreator } from '../helpers/financialYearHelper'
import { isSystemAdmin } from '../helpers/permissionHelper'

const useStyles = makeStyles({
   addBtn: {
      '@media screen and (max-width: 640px)': {
         display: 'inline-flex',
      },
   },
   row: {
      '& >': {
         ':nth-child(1)': {
            width: '20%',
         },
         ':nth-child(2)': {
            width: '20%',
         },
         ':nth-child(3)': {
            width: '20%',
         },
         ':nth-child(4)': {
            width: '40%',
         },
      },
   },
})

export const RiskEconomy = () => {
   const classes = useStyles()
   const navigate = useNavigate()
   const dataContext = useContext(DataContext)
   const { userAccount } = dataContext.state
   const [searchParams, setSearchParams] = useSearchParams()
   const selectedTab = searchParams.get('tab')
   const [riskEconomyState, setRiskEconomyState] = useState<IListState<FinancialYear>>({
      loading: true,
      open: false,
      allItems: [],
      filteredItems: [],
   })
   const { organisation } = dataContext.state
   const { loading, allItems, filteredItems } = riskEconomyState
   const [workplaces, setWorkplaces] = useState<Workplace[]>([])

   useEffect(() => {
      if (!selectedTab) {
         setSearchParams({ tab: RiskEconomyTabs.Ongoing })
         return
      }
      if (!organisation?.id) return
      const ac = new AbortController()
      ;(async () => {
         try {
            const [financialYearsRes, workplacesRes] = await Promise.all([
               api().getFinancialYearsByOrganisationId(organisation.id, { signal: ac.signal }),
               api().getWorkplacesByOrganisationId(organisation.id, { signal: ac.signal }),
            ])
            const allItems = financialYearsRes.data ?? []
            const filteredItems =
               selectedTab === RiskEconomyTabs.Ongoing
                  ? allItems.filter((f) => f.status !== FinancialYearStatus.Completed)
                  : allItems.filter((f) => f.status === FinancialYearStatus.Completed)
            setWorkplaces(workplacesRes.data)
            setRiskEconomyState((prev) => ({ ...prev, loading: false, allItems, filteredItems, workplaces: workplacesRes.data }))
         } catch (error: any) {
            if (error?.name !== 'AbortError') throw error
         }
      })()
      return () => ac.abort()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedTab, dataContext.state.userAccount?.id, organisation?.id])

   useEffect(() => {
      if (selectedTab === RiskEconomyTabs.Ongoing)
         setRiskEconomyState((prev) => ({ ...prev, filteredItems: prev.allItems.filter((f) => f.status !== FinancialYearStatus.Completed) }))
      if (selectedTab === RiskEconomyTabs.Concluded)
         setRiskEconomyState((prev) => ({ ...prev, filteredItems: prev.allItems.filter((f) => f.status === FinancialYearStatus.Completed) }))
   }, [selectedTab, allItems.length])

   return (
      <Layout
         activeDesktopLink={NavigationLink.RiskEconomy}
         breadCrumbItems={[{ title: t('RiskEconomy'), active: true, path: NavigationLink.RiskEconomy }]}
         tabs={
            <>
               <Tab value={RiskEconomyTabs.Ongoing}>{t('Ongoing')}</Tab>
               <Tab value={RiskEconomyTabs.Concluded}>{t('Concluded')}</Tab>
            </>
         }
         button={
            <Button
               className={classes.addBtn}
               appearance="primary"
               onClick={() => setRiskEconomyState((prev) => ({ ...prev, open: true }))}
               icon={<AddCircle24Regular />}
            >
               {t('NewFinancialYear')}
            </Button>
         }
      >
         <Overview
            loading={loading}
            columns={getRiskEconomyColumns(setRiskEconomyState, userAccount)}
            items={filteredItems}
            onRowClick={(item) => navigate(`/risk-economy/${item.id}`)}
            keyValue={'name'}
            noItemsText={
               selectedTab === RiskEconomyTabs.Ongoing
                  ? t('TimeToCreateYourFirstFinancialYear')
                  : t('NoConcludedItems', { Item: t('FinancialYear').toLowerCase() })
            }
            title={
               selectedTab === RiskEconomyTabs.Ongoing
                  ? t('OngoingItem', { Item: t('FinancialYear').toLowerCase() })
                  : t('ConcludedItem', { Item: t('FinancialYear').toLowerCase() })
            }
            rowClassName={classes.row}
            renderMenuButton={(item) => (
               <MoreOptionsMenuButton
                  disabled={!isFinancialYearOwnerOrCreator(item, userAccount) && !isSystemAdmin(userAccount)}
                  disabledToolTipMessage={t('DeleteFinancialYearNoAccessMessage')}
                  item={item}
                  deleteFunction={deleteFinancialYear}
                  setState={setRiskEconomyState}
                  deleteMessage={t('DeleteItem', { Item: t('FinancialYear').toLowerCase() })}
                  deleteConfirmationMessage={t('DeleteItemConfirmationMessage', { Item: t('FinancialYear').toLowerCase() })}
               />
            )}
         />
         <ModalAddFinancialYear setRiskEconomyState={setRiskEconomyState} riskEconomyState={riskEconomyState} workplaces={workplaces} />
      </Layout>
   )
}
